import { Clock, X, CheckCircle, AlarmClock } from 'lucide-react';
import { useReminders } from '@/contexts/ReminderContext';
import { formatDate } from '@/lib/dateFormat';

function formatDue(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 0) {
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `Due ${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  }
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Due now';
  if (mins < 60) return `${mins}m overdue`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h overdue`;
  return formatDate(d);
}

export function ReminderPopup() {
  const { dueReminders, snooze, markDone, dismiss } = useReminders();

  if (dueReminders.length === 0) return null;

  const reminder = dueReminders[0];
  const extra = dueReminders.length - 1;

  return (
    <>
      {/* Screen-center overlay backdrop */}
      <div className="fixed inset-0 z-[90] bg-black/20 pointer-events-none" />

      {/* Centered modal */}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div
          className="w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto"
          style={{ animation: 'popIn 0.25s ease-out' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 bg-[#6366F1]">
            <div className="flex items-center gap-2 text-white">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-semibold">Reminder Due</span>
              {extra > 0 && (
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                  +{extra} more
                </span>
              )}
            </div>
            <button
              onClick={() => dismiss(reminder.id)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <p className="font-bold text-[#1E293B] text-lg mb-1">{reminder.title}</p>
            {reminder.lead && (
              <p className="text-sm text-[#64748B] mb-2">
                Lead: <span className="font-medium text-[#6366F1]">{reminder.lead.name}</span>
              </p>
            )}
            <div className="flex items-center gap-1.5 text-sm text-[#10B981] font-medium">
              <Clock className="h-4 w-4" />
              {formatDue(reminder.dueAt)}
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-5 flex flex-wrap gap-2">
            <button
              onClick={() => snooze(reminder.id, 15)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-[#64748B] text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <AlarmClock className="h-4 w-4" />
              Snooze 15m
            </button>
            <button
              onClick={() => snooze(reminder.id, 60)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-[#64748B] text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <AlarmClock className="h-4 w-4" />
              Snooze 1h
            </button>
            <button
              onClick={() => markDone(reminder.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              Mark Done
            </button>
            <button
              onClick={() => dismiss(reminder.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-[#64748B] text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
}
